import { TOKEN_EXPIRY_MINUTES } from '@/lib/authConstants';
import api from './core';
import { User } from './types';
import axios from 'axios';

interface LoginResponse {
  access: string;
  refresh: string;
}

interface UserResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_superuser: boolean;
  last_active_customer?: string;
  customer_access: CustomerAccess[];
  profile?: {
    theme?: 'dark' | 'light' | 'auto';
    last_active_customer?: string;
    last_active_site?: number | null;
  };
}

interface CustomerAccess {
  customer: import('./types').Customer;
  is_admin: boolean;
}

export const getTokenPair = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>(`/token/pair`, {
    username: email, // Using email as username
    password,
  });

  // Store tokens and set expiry
  localStorage.setItem('access_token', response.data.access);
  localStorage.setItem('refresh_token', response.data.refresh);
  
  // Set token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
  const tokenExpiry = new Date();
  tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
  localStorage.setItem('token_expiry', tokenExpiry.toISOString());

  // Set the Authorization header for subsequent requests
  api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;

  return response.data;
};

export const refreshAuthToken = async (refreshToken: string): Promise<LoginResponse> => {
  try {
    const response = await api.post<LoginResponse>(`/token/refresh`, {
      refresh: refreshToken,
    });

    // Store tokens and set expiry
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);

    // Set token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
    const tokenExpiry = new Date();
    tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
    localStorage.setItem('token_expiry', tokenExpiry.toISOString());

    // Set the Authorization header for subsequent requests
    api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;

    return response.data;
  } catch (error) {
    // Re-throw; caller (lib/auth.ts refreshToken) and core.ts handle 401 and clear storage / redirect
    throw error;
  }
};

export const verifyAuthToken = async (token: string): Promise<void> => {
  try {
    await api.post(`/token/verify`, {
      token,
    });
  } catch (error) {
    // Let the error propagate up - it will be handled at a higher level
    throw error;
  }
};

export const getCurrentUser = async (): Promise<UserResponse> => {
  const response = await api.get<UserResponse>(`/crm/users/me/`);
  return response.data;
};

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  return getTokenPair(email, password);
};
